import useTable from '@ui-components-3/ui/lib/components/Table/hooks/useTable'
import Table from './CoreTicketComponents/ClientPaginatedTable'
// import TestIds from 'util/TestIds'
import { Ticket } from './types'
import useMemberTickets from 'core/hooks/member/useMemberTickets'
import { SortingState } from '@ui-components-3/ui/lib/components/Table/types'
import { useCallback, useState, useEffect } from 'react'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import MemberTicketDetailsView from './MemberTicketDetailsView'
import Badge from 'components/common/Badge'
import { formatTicketDate, getTicketStatusLabel, ticketStatusBadgeVariants } from './util'
import { useTicketContext } from './TicketContext'

const columns = [
  {
    id: 'status',
    // Warning: Sorting function won't trigger without even an arbitrary (when using custom cell renderer) accessorFn
    accessorFn: (ticket: Ticket) => ticket,
    header: 'Ticket Status',
    enableSorting: true,
    meta: {
      cellClassName: 'min-w-[150px]',
    },
    cell: (cell: any) => {
      const { status } = cell?.row?.original || { status: 'new' }

      return <Badge variant={ticketStatusBadgeVariants[status] || 'neutral'}>{getTicketStatusLabel(status)}</Badge>
    },
  },
  {
    id: 'externalTicketId',
    accessorFn: (ticket: Ticket) => `#${ticket.externalTicketId}`,
    header: 'ID',
    enableSorting: true,
    meta: {
      cellClassName: 'min-w-[100px]',
    },
  },
  {
    id: 'referralType',
    accessorFn: (ticket: Ticket) => ticket.referralType ?? '-',
    header: 'Referral Type',
    enableSorting: true,
    meta: {
      cellClassName: 'w-[150px]',
    },
  },
  {
    id: 'noteType',
    accessorFn: (ticket: Ticket) => ticket.noteType ?? 'Crisis Response',
    header: 'Note Type',
    enableSorting: false,
    meta: {
      cellClassName: 'w-[200px]',
    },
  },
  {
    id: 'subject',
    accessorFn: (ticket: Ticket) => ticket.subject ?? '-',
    header: 'Subject',
    enableSorting: false,
    meta: {
      cellClassName: 'w-[200px]',
    },
  },
  {
    id: 'requester',
    accessorFn: (ticket: Ticket) => ticket.requester ?? '-',
    header: 'Requester',
    enableSorting: false,
    meta: {
      cellClassName: 'w-[200px] whitespace-nowrap',
    },
  },
  {
    id: 'externalUpdatedAt',
    accessorFn: (ticket: Ticket) => formatTicketDate(ticket?.externalUpdatedAt),
    header: 'Updated',
    enableSorting: true,
    meta: {
      cellClassName: 'w-[200px] whitespace-pre',
    },
  },
]

export type MemberTicketsTableProps = {
  memberId: string
}

const MemberTicketsTable = ({ memberId }: MemberTicketsTableProps) => {
  const { selectedTicketId, setSelectedTicketId } = useTicketContext()
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([])

  const { memberTickets, isLoading, totalCount } = useMemberTickets(memberId, pagination, sorting)

  const table = useTable({
    rowId: 'externalTicketId',
    paginationType: 'server',
    columns: columns as any,
    data: memberTickets ?? [],
    rowCount: totalCount ?? 0,
    isLoading: isLoading,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  })

  const onRowClick = useCallback(
    (row: { original: Ticket }) => {
      const ticketId = row?.original?.ticketId || null
      setSelectedTicketId(ticketId)
    },
    [setSelectedTicketId],
  )

  const selectedTicket = selectedTicketId ? memberTickets?.find((ticket) => ticket.ticketId === selectedTicketId) : null

  return (
    <>
      <div className={classNames(selectedTicketId ? 'hidden' : 'block')}>
        <Table
          model={table}
          aria-label="Member Tickets Table"
          containerClassName="min-h-[425px]"
          onRowClick={onRowClick}
        />
      </div>
      {!!selectedTicketId && selectedTicket && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <MemberTicketDetailsView ticketId={selectedTicketId} ticketData={selectedTicket} />
        </div>
      )}
    </>
  )
}

export default MemberTicketsTable
